import React, { FunctionComponent, useEffect, useState } from "react";
import {
  Box,
  IconButton,
  InputBase,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@material-ui/core";
import moment from "moment";
import DescriptionIcon from "@material-ui/icons/Description";
import SearchIcon from "@material-ui/icons/Search";
import { IBillDetails } from "./Bill";
import { TablePaginationActions } from "../TablePagination";

interface IRecentBills {
  data: IBillDetails[];
  billNumberValidation: (bill_number: string) => void;
}

const useStyles: any = makeStyles((theme: { [key: string]: any }) => ({
  recentbillsTableRoot: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  recentbillsTable: {
    minWidth: 1090,
  },
  flexRow: {
    display: "flex",
    flexDirection: "row",
  },
  circledHighlight: {
    borderRadius: "50%",
    backgroundColor: "#B1D8B7",
    fontSize: "15px",
    fontWeight: "bold",
    color: "#3D550C",
    padding: "13px 16px",
    width: 40,
    height: 40,
    position: "relative",
  },
  printButton: {
    color: "#76B947",
    cursor: "pointer",
    padding: "10px",
    fontSize: "15px",
    fontWeight: "bold",
    "&:hover": {
      color: "#59981A",
    },
  },
  mainText: {
    fontSize: "15px",
    fontWeight: "bold",
    color: "#233298",
  },
  subText: {
    fontSize: "12px",
    fontWeight: "normal",
    color: theme.palette.text.secondary,
  },
  searchInput: {
    marginLeft: theme.spacing(1),
    flex: 1,
    color: "#fefefe",
    borderBottom: "1px solid #aaaaaa",
  },
  searchPaper: {
    padding: "2px 4px",
    display: "flex",
    alignItems: "center",
    width: 400,
  },
  tableHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.palette.primary.main,
  },
}));

type Order = "asc" | "desc";
function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: Order,
  orderBy: Key
): (
  a: { [key in Key]: number | string },
  b: { [key in Key]: number | string }
) => number {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

interface TableProps {
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof IBillDetails
  ) => void;
  order: Order;
  orderBy: string;
}

interface HeadCell {
  id: keyof IBillDetails;
  label: string;
  numeric: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "billnumber",
    numeric: true,
    label: "Bill Number(s)",
  },
  {
    id: "billdocnumber",
    numeric: true,
    label: "Document Number",
  },
  {
    id: "billterminal",
    numeric: false,
    label: "Terminal Number",
  },
  {
    id: "billamount",
    numeric: true,
    label: "Total",
  },
];

function TableHeader(props: TableProps) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property: keyof IBillDetails) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            // align={headCell.numeric ? "right" : "left"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell></TableCell>
      </TableRow>
    </TableHead>
  );
}

const RecentBillsTable: FunctionComponent<IRecentBills> = ({
  data,
  billNumberValidation,
}) => {
  const classes = useStyles();

  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<keyof IBillDetails>("billdate");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchtext] = useState<string | null>("");
  const [filteredData, setFilteredData] = useState<IBillDetails[]>([]);

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof IBillDetails
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    setPage(0);
    if (searchText) {
      const filtered = data.filter(
        (data) =>
          data.billdocnumber?.includes(searchText) ||
          data.billnumber?.includes(searchText) ||
          data.billamount?.includes(searchText)
      );
      setFilteredData(filtered);
    } else setFilteredData(data);
  }, [data, searchText]);

  return (
    <Paper className={classes.recentBillsTableRoot}>
      <Box className={classes.tableHead}>
        <Typography
          component="div"
          color="secondary"
          style={{ marginLeft: "1em" }}
        >
          <Box fontSize="h5.fontSize" fontWeight="fontWeightBold">
            Recent Bills
          </Box>
        </Typography>
        <Box className={classes.searchPaper}>
          <InputBase
            className={classes.searchInput}
            value={searchText}
            placeholder="Search"
            inputProps={{ "aria-label": "search " }}
            onChange={(event) => setSearchtext(event.target.value)}
          />
          <IconButton aria-label="search" color="secondary">
            <SearchIcon />
          </IconButton>
        </Box>
      </Box>
      <Table className={classes.recentBillsTable}>
        <TableHeader
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
        />
        <TableBody>
          {!!filteredData.length ? (
            filteredData
              .slice()
              .sort(getComparator(order, orderBy))
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((item, index) => (
                <TableRow
                  key={`${item.billnumber}-${item.billdocnumber}-${item.billterminal}-${item.billdate}-${item.billamount}`}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f3f3f3" : "#fefefe",
                  }}
                >
                  <TableCell>
                    <Typography
                      style={{
                        fontSize: "15px",
                        fontWeight: "bold",
                      }}
                    >
                      {item?.billnumber ?? "---"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <div className={classes.flexRow}>
                      <span className={classes.circledHighlight}>
                        &nbsp;&nbsp;&nbsp;&nbsp;
                        <DescriptionIcon
                          style={{ position: "absolute", top: 8, left: 8 }}
                        />
                      </span>
                      <span style={{ marginLeft: 10 }}>
                        <Typography className={classes.mainText}>
                          {item?.billdocnumber ?? "---"}
                        </Typography>
                        <Typography className={classes.subText}>
                          {item?.billdate
                            ? moment(item.billdate).format(
                                "DD MMM YYYY, h:mm:ss a"
                              )
                            : "---"}
                        </Typography>
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={classes.circledHighlight}>
                      {item?.billterminal ?? "---"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Typography
                      style={{
                        fontSize: "15px",
                        fontWeight: "bold",
                      }}
                    >
                      {item?.billamount ?? "---"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <span
                      onClick={() => {
                        billNumberValidation(item?.billdocnumber ?? "");
                      }}
                      className={classes.printButton}
                    >
                      Print
                    </span>
                  </TableCell>
                </TableRow>
              ))
          ) : (
            <TableRow
              style={{
                backgroundColor: "#fefefe",
              }}
            >
              <TableCell colSpan={5} align="center">
                <Typography
                  style={{
                    fontSize: "15px",
                    fontWeight: "bold",
                  }}
                  color="error"
                >
                  No bill found
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        <TableFooter>
          <TableRow>
            <TablePagination
              rowsPerPageOptions={
                filteredData.length > 0 ? [5, 10, 20, 50, 80, 100] : [0]
              }
              colSpan={6}
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              SelectProps={{
                inputProps: { "aria-label": "rows per page" },
                native: true,
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </Paper>
  );
};

export default RecentBillsTable;
